import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MDCTabBar } from '@material/tab-bar';
import { format } from 'date-fns';
import GetLangConfig from '../../../util/LangUtil';
import FormatDateLocale from '../../../util/DateUtil';
import './dashboard.css';
import { DrugExceptionsClient } from '../../../client/drug-exceptions-client';
import { DrugExceptionStatus, ClaimantResponse } from '../../../model/Reformulary';
import { useLanguage } from '../../../context/LanguageContext';

interface HandleError {
    handleError: Function;
}

export default function ClaimantDashboard(props: HandleError) {

    const langConfig = GetLangConfig('dashboard');
    const history = useNavigate();
    const language = useLanguage();
    const userId = sessionStorage.getItem('userId') || '';
    const claimantId = JSON.parse(userId);
    const drugExceptionsClient = new DrugExceptionsClient();
    const exceptionStatus = new Map();
    exceptionStatus.set(DrugExceptionStatus[DrugExceptionStatus.UNDER_REVIEW], langConfig.general.status.underreview );
    exceptionStatus.set(DrugExceptionStatus[DrugExceptionStatus.INCOMPLETE], langConfig.general.status.incomplete );
    exceptionStatus.set(DrugExceptionStatus[DrugExceptionStatus.APPROVED], langConfig.general.status.approved );
    exceptionStatus.set(DrugExceptionStatus[DrugExceptionStatus.NOT_APPROVED], langConfig.general.status.notapproved);
    exceptionStatus.set(DrugExceptionStatus[DrugExceptionStatus.ASSESSED_APPROVED], langConfig.general.status.underreview);
    exceptionStatus.set(DrugExceptionStatus[DrugExceptionStatus.ASSESSED_NOT_APPROVED], langConfig.general.status.underreview);
    exceptionStatus.set(DrugExceptionStatus[DrugExceptionStatus.EXPIRED], langConfig.general.status.expired );


    const [exceptionList, updateExceptionList] = useState( [] );
    const [tab, updateTab] = useState('requests');
    const tabsEl = useRef<HTMLDivElement>(null);

    async function getExceptionList() {
        let response;
        if (claimantId) {
            const token = claimantId.authToken;
            const id = claimantId.id;
            if (token && id) {
                response = await drugExceptionsClient.api.get('/drugexceptions',  {
                    headers:{
                        Authorization: `Bearer ${token}`
                    }
                });
            }
            return response;
        }
    }

    function getStatusText(ref: string) {
        return exceptionStatus.get(ref);
    }

    function requestsView() {

        let view;

        if (exceptionList === undefined) {
            view = <div>no exceptions</div>;
        } else {
            view = exceptionList
                .filter((item: any) => item.drugExceptionStatus !== DrugExceptionStatus[DrugExceptionStatus.EXPIRED])
                .reverse()
                .map((exception: {
                        id: string;
                        claimant: ClaimantResponse;
                        submittedDate: number;
                        drugExceptionStatus: string;
                    }, index: number) => {
                        return <div key={index} className="exception">
                            <div>
                                {langConfig.dashboard.label5}
                            </div>
                            <div>
                                <div>#{exception.id}</div>
                                <div>{langConfig.dashboard.label6}</div>
                            </div>
                            <div>
                                <div>{FormatDateLocale(new Date(exception.submittedDate), language)}</div>
                                <div>{langConfig.dashboard.label7}</div>
                            </div>
                            <div>
                                <div>{exception.claimant?.firstName} {exception.claimant?.lastName}</div>
                                <div>{langConfig.dashboard.label8}</div>
                            </div>
                            <div>
                                <div>{exception.claimant?.memberFirstName} {exception.claimant?.memberLastName}</div>
                                <div>{langConfig.dashboard.label9}</div>
                            </div>
                            <div></div>
                            <div>
                                <div>{getStatusText(exception.drugExceptionStatus)}</div>
                                <div>{langConfig.dashboard.label10}</div>
                            </div>
                            <div></div>
                        </div>;
                    }
                );
        }

        return view;
    }

    function pastRequestsView() {
        let view;

        if (exceptionList === undefined) {
            view = <div>no exceptions</div>;
        } else {
            view = exceptionList
                .filter((item: any) => item.drugExceptionStatus === DrugExceptionStatus[DrugExceptionStatus.EXPIRED])
                .sort((a: { id: number }, b: { id: number }) => b.id - a.id)
                .map((exception: {
                        id: string;
                        submittedDate: number;
                        drugExceptionStatus: string;
                    }, index: number) => {
                        return <div key={index} className="exception">
                            <div>
                                {langConfig.dashboard.label11}
                            </div>
                            <div>
                                <div>#{exception.id}</div>
                                <div>{langConfig.dashboard.label6}</div>
                            </div>
                            <div>
                                <div>{format(new Date(exception.submittedDate), 'yyyy MMM dd ')}</div>
                                <div>{langConfig.dashboard.label7}</div>
                            </div>
                            <div>
                                <div>{getStatusText(exception.drugExceptionStatus)}</div>
                                <div>{langConfig.dashboard.label10}</div>
                            </div>
                            <div>{langConfig.dashboard.label12}</div>
                        </div>;
                    }
                );
        }

        return view;
    }

    useEffect(()=>{
        if (exceptionList.length === 0) {
            getExceptionList()
                .then((data) => {
                    if (data) {
                        updateExceptionList(data.data);
                    }
                });
        }
    }, []);

    useEffect(()=>{
        const tabsNode = tabsEl.current;
        if (tabsNode) {
            const tabBar = new MDCTabBar(tabsNode);
        }
    });

    return (
        
        <div className="dashboard-container" ref={tabsEl}>
            <div className={'header ' + (langConfig.general.lang == 'fr' ? 'fr' : '')}>
                <h1>{langConfig.dashboard.header1}</h1>
                <a href={langConfig.general.newexceptionform} download className="mdc-button mdc-button--raised mdc-ripple-upgrade button-shaped ">
                    <div className="mdc-button__ripple"></div>
                     <span className="mdc-button__label">{langConfig.dashboard.label1}</span>
                </a>
                <button className="mdc-button mdc-button--raised mdc-ripple-upgrade button-shaped" onClick={e=>history('/exception')}>
                    <div className="mdc-button__ripple"></div>
                    <span className="mdc-button__label">{langConfig.dashboard.label2}</span>
                </button>
            </div>

            <div className="tabs-container">
                <div className="mdc-tab-bar" role="tablist">
                    <div className="mdc-tab-scroller">
                        <div className="mdc-tab-scroller__scroll-area">
                            <div className="mdc-tab-scroller__scroll-content">
                                <button className="mdc-tab mdc-tab--active" role="tab"  aria-selected="true" onClick={e=>updateTab('requests')}>
                                    <span className="mdc-tab__content">
                                        <span className="mdc-tab__icon material-icons" aria-hidden="true"></span>
                                        <span className="mdc-tab__text-label">{langConfig.dashboard.label3}</span>
                                    </span>
                                    <span className="mdc-tab-indicator mdc-tab-indicator--active">
                                        <span className="mdc-tab-indicator__content mdc-tab-indicator__content--underline"></span>
                                    </span>
                                    <span className="mdc-tab__ripple"></span>
                                </button>
                                <button className="mdc-tab" role="tab"  aria-selected="true" onClick={e=>updateTab('past-requests')}>
                                    <span className="mdc-tab__content">
                                        <span className="mdc-tab__icon material-icons" aria-hidden="true"></span>
                                        <span className="mdc-tab__text-label">{langConfig.dashboard.label4}</span>
                                    </span>
                                    <span className="mdc-tab-indicator">
                                        <span className="mdc-tab-indicator__content mdc-tab-indicator__content--underline"></span>
                                    </span>
                                    <span className="mdc-tab__ripple"></span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="tables-container">
                {
                    tab === 'requests'
                        ? requestsView()
                        : pastRequestsView()
                }

            </div>

        </div>
    );

}
